"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Calendar, CircleCheck as CheckCircle2, Clock, Loader as Loader2, Mail, Phone, ShieldCheck, UserCheck, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const guarantees = [{
  id: 1,
  title: "100% Direct Consultant Access",
  text: "You speak and collaborate directly with me, Yassine Essaadi. No account managers or junior handlers."
}, {
  id: 2,
  title: "Customized Search Strategy",
  text: "Every SEO strategy is crafted specifically for your target audience, commercial goals, and niche."
}, {
  id: 3,
  title: "Practical & Transparent Deliverables",
  text: "Clear, step-by-step optimization plans with straightforward reporting focused on actual revenue growth."
}, {
  id: 4,
  title: "Honest & Realistic Advice",
  text: "No empty promises or automated template audits. Just expert guidance to improve rankings and leads."
}];
function ConsultationFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-muted/50 border border-border rounded-xl text-center space-y-4">
        <div className="w-14 h-14 bg-accent/20 text-accent rounded-full flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-7 h-7" />
        </div>
        <h3 className="text-2xl font-bold text-foreground font-serif">Consultation Request Received</h3>
        <p className="text-muted-foreground text-sm leading-relaxed">{successMessage}</p>
        <p className="text-xs text-muted-foreground">{"I will personally review your site details and email you within 24 hours."}</p>
        <Button onClick={resetForm} variant="outline" size="sm" className="mt-4">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="name" rules={{
      required: "Please enter your name"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground text-xs font-semibold uppercase tracking-wider">Your Name</FormLabel>
            <FormControl>
              <Input placeholder="e.g. David Miller" className="bg-background text-foreground px-3.5 py-2 text-sm rounded-lg" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email address is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground text-xs font-semibold uppercase tracking-wider">Work Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="david@company.com" className="bg-background text-foreground px-3.5 py-2 text-sm rounded-lg" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="website" rules={{
      required: "Please enter your website URL"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground text-xs font-semibold uppercase tracking-wider">Website URL</FormLabel>
            <FormControl>
              <Input type="text" placeholder="https://yourwebsite.com" className="bg-background text-foreground px-3.5 py-2 text-sm rounded-lg" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="goal" rules={{
      required: false
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground text-xs font-semibold uppercase tracking-wider">Main SEO Goal (Optional)</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Increase organic sales, fix technical issues..." className="bg-background text-foreground px-3.5 py-2 text-sm rounded-lg" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive font-medium">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 bg-accent hover:bg-accent/90 text-accent-foreground font-semibold rounded-lg text-base shadow-md transition-all cursor-pointer mt-2">
        {isSubmitting ? <>
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Processing Request...
          </> : <>
            Book a Free SEO Consultation
            <ArrowRight className="w-5 h-5 ml-1" />
          </>}
      </Button>

      <p className="text-center text-xs text-muted-foreground pt-1 flex items-center justify-center gap-1.5">
        <ShieldCheck className="w-4 h-4 text-primary" />
        <span>100% free consultation. No high-pressure sales pitch.</span>
      </p>
    </Form>;
}
export default function Consultation() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="strongconsultationcta" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          x: -20
        }} whileInView={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-7 space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold tracking-wide uppercase border border-border">
                <Sparkles className="w-3.5 h-3.5 text-accent" />
                <span>Direct Expert Access</span>
              </div>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground leading-tight tracking-tight">
                Ready to Grow Your Search Visibility with a Dedicated SEO Partner?
              </h2>

              <p className="text-lg text-muted-foreground leading-relaxed">
                {"Book a free 30-minute strategic consultation with me. We'll review your current website performance, identify hidden search traffic opportunities, and define clear next steps for sustainable organic growth."}
              </p>
            </div>

            <div className="p-5 sm:p-6 bg-card border border-border rounded-2xl shadow-sm flex flex-col sm:flex-row gap-5 items-start sm:items-center">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_consultant.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_consultant.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_consultant.webp-wvc-sizes" alt="Yassine Essaadi - Independent SEO Consultant" loading="lazy" className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover shrink-0 border border-border shadow-xs" />
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-foreground text-lg font-serif">Yassine Essaadi</h3>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-accent/20 text-accent text-xs font-medium">
                    <UserCheck className="w-3.5 h-3.5" />
                    SEO Specialist
                  </span>
                </div>
                <p className="text-sm text-foreground/80 leading-relaxed">
                  {"“I work directly with UK and international businesses to turn search engine visibility into reliable revenue. You get my hands-on expertise on every call and campaign.”"}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {guarantees.map((item, i) => <div data-index={i} key={item.id} className="p-4 rounded-xl bg-card border border-border space-y-2 hover:border-primary/40 transition-[border-color,box-shadow] duration-200 shadow-2xs">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-accent shrink-0" />
                    <h4 className="font-semibold text-foreground text-sm font-serif">{item.title}</h4>
                  </div>
                  <p className="text-xs text-muted-foreground leading-normal pl-7">
                    {item.text}
                  </p>
                </div>)}
            </div>

            <Separator className="bg-border my-6" />

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-6">
                <a href="mailto:contact@ye-consultancy.com" className="inline-flex items-center gap-2 text-foreground font-medium hover:text-primary transition-colors">
                  <Mail className="w-4 h-4 text-primary" />
                  <span>contact@ye-consultancy.com</span>
                </a>
                <a href="tel:+447490984430" className="inline-flex items-center gap-2 text-foreground font-medium hover:text-primary transition-colors">
                  <Phone className="w-4 h-4 text-primary" />
                  <span>+44 7490984430</span>
                </a>
              </div>

              <div className="flex items-center gap-2 text-xs">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span>Response time: under 24 hours</span>
              </div>
            </div>

            <div className="pt-2">
              <Button asChild variant="outline" size="sm" className="rounded-lg">
                <Link to="/CaseStudiesPage">
                  <span>Explore Verified Case Studies</span>
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </div>
          </motion.div>

          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-5">
            <div className="bg-card border border-border p-6 sm:p-8 rounded-2xl shadow-xl space-y-6 relative">
              <div className="flex items-center justify-between border-b border-border pb-4">
                <div>
                  <h3 className="text-xl font-bold font-serif text-foreground">Schedule Your Consultation</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">{"Choose a convenient time or submit details below"}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <SafeHydrate><Calendar className="w-5 h-5" /></SafeHydrate>
                </div>
              </div>

              <FormProvider formId="consultation-cta-form" sectionName="strong-consultation-cta" defaultValues={{
              name: "",
              email: "",
              website: "",
              goal: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              website: {
                required: true
              }
            }} submitText="Book a Free SEO Consultation" successMessage="Thank you! Your request has been received. I will inspect your website and follow up with available time slots within 24 hours.">
                <ConsultationFormContent />
              </FormProvider>

              <div className="bg-muted/60 p-4 rounded-xl border border-border/60 text-xs text-muted-foreground space-y-1">
                <p className="font-medium text-foreground">{"What happens next?"}</p>
                <p>{"1. I perform a initial search audit of your domain."}</p>
                <p>{"2. We connect for a 30-minute direct call."}</p>
                <p>{"3. You receive a tailored action plan with actionable priorities."}</p>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}